<script setup lang="ts">
import { Button } from '@/components/ui/button';
import EmptyState from '@/components/ui/empty-state/EmptyState.vue';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import SectionSurface from '@/components/workspace/SectionSurface.vue';
import ProposalSummaryRow from '@/components/proposals/ProposalSummaryRow.vue';
import { ExternalLink, FileText, MoreHorizontal, Pencil, Receipt, Send } from 'lucide-vue-next';
import type { ProjectContract, Proposal } from '~/types/models';
import type { ClientDeliveryContractItem } from './types';
import ContractSummaryRow from '@/components/contracts/ContractSummaryRow.vue';

interface Props {
    canMutate: boolean;
    canCreateContract: boolean;
    clientContracts: ClientDeliveryContractItem[];
    clientProposals: Proposal[];
    clientId?: string;
    clientContract?: ProjectContract | null;
}

const props = withDefaults(defineProps<Props>(), {
    clientContract: null,
    clientId: '',
});
const { tenantPath } = useTenantPath();

const emit = defineEmits<{
    (e: 'create-contract'): void,
    (e: 'manage-client-contract'): void,
    (e: 'download-client-contract-pdf'): void,
}>();

</script>

<template>
    <SectionSurface eyebrow="Agreements" title="Proposals and Contracts" description="Client proposals and signed or draft agreements tied to this account." :icon="FileText" density="compact" presentation="editorial">
        <template v-if="canMutate" #actions>
            <Button v-if="clientId" variant="outline" size="sm" @click="navigateTo(tenantPath({ path: '/proposals/new', query: { clientId } }))">
                Create proposal
            </Button>
            <Button v-if="canCreateContract" variant="outline" size="sm" @click="emit('create-contract')">
                <FileText class="mr-2 size-4" />
                Create contract
            </Button>
        </template>

        <EmptyState v-if="clientContracts.length === 0 && clientProposals.length === 0" embedded title="No proposals or contracts linked yet" description="Client proposals and contracts will appear here." />

        <div v-else class="mt-4 space-y-5">
            <div v-if="clientProposals.length">
                <p class="px-4 pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Proposals</p>
                <div class="divide-y divide-border/55">
                    <ProposalSummaryRow
                        v-for="proposal in clientProposals"
                        :key="proposal.id"
                        :proposal="proposal"
                        :href="tenantPath(`/proposals/${proposal.id}`)"
                        compact
                        surface="list"
                    />
                </div>
            </div>
            <div v-if="clientContracts.length" class="divide-y divide-border/55">
                <p class="px-4 pb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Contracts</p>
            <ContractSummaryRow
                v-for="contractItem in clientContracts"
                :key="contractItem.id"
                :title="contractItem.title"
                :summary="contractItem.summary"
                :scope-label="contractItem.scopeLabel"
                :version="contractItem.id.startsWith('client:') ? props.clientContract?.version : null"
                :status="contractItem.status"
                :status-variant="contractItem.badgeVariant"
                :updated-at="contractItem.updatedAt"
            >
                <template #menu>
                    <DropdownMenu>
                        <DropdownMenuTrigger as-child><Button variant="ghost" size="icon" class="size-8 rounded-full opacity-70 transition-opacity hover:opacity-100 group-hover/contract:opacity-100" aria-label="Contract actions"><MoreHorizontal class="size-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem v-if="contractItem.id.startsWith('client:')" @select="emit('manage-client-contract')"><Pencil class="mr-2 size-4" />Manage contract</DropdownMenuItem>
                            <DropdownMenuItem v-if="contractItem.id.startsWith('client:') && props.clientContract?.status === 'draft'" @select="emit('manage-client-contract')"><Send class="mr-2 size-4" />Send contract</DropdownMenuItem>
                            <DropdownMenuItem v-if="contractItem.id.startsWith('client:') && props.clientContract && props.clientContract.status !== 'draft'" @select="emit('download-client-contract-pdf')"><Receipt class="mr-2 size-4" />Download signed PDF</DropdownMenuItem>
                            <DropdownMenuItem v-if="contractItem.href" as-child><NuxtLink :to="contractItem.href"><ExternalLink class="mr-2 size-4" />Open contract</NuxtLink></DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </template>
                <template #action>
                    <Button
                        v-if="contractItem.id.startsWith('client:')"
                        variant="link"
                        size="sm"
                        class="mt-2 h-auto p-0 text-xs"
                        @click="props.clientContract?.status === 'draft' ? emit('manage-client-contract') : emit('download-client-contract-pdf')"
                    >
                        <Send v-if="props.clientContract?.status === 'draft'" class="mr-1.5 size-3.5" />
                        <Receipt v-else class="mr-1.5 size-3.5" />
                        {{ props.clientContract?.status === 'draft' ? 'Prepare and send contract' : 'Download signed PDF' }}
                    </Button>
                    <Button v-else-if="contractItem.href" variant="link" size="sm" class="mt-2 h-auto p-0 text-xs" as-child>
                        <NuxtLink :to="contractItem.href"><ExternalLink class="mr-1.5 size-3.5" />Open project contract</NuxtLink>
                    </Button>
                </template>
            </ContractSummaryRow>
            </div>
        </div>
    </SectionSurface>
</template>
